/**
 * Send Notification — a CP nudges an attributed customer about one service.
 *
 * Exactly one of: a preset (Document Pending / Action Required) or a
 * non-empty custom message. A 202 means *accepted*: delivery (dashboard,
 * email, WhatsApp where configured) runs afterwards, so the copy never
 * claims the message was delivered. It changes nothing on the service.
 */
import * as React from 'react';
import { CheckCircle2, MessageSquareText, Send } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorMessage,
  Label,
  Textarea,
  cn,
  toast,
} from '@finmin/ui';
import {
  useSendChannelPartnerNotification,
  type ChannelPartnerNotificationPayload,
  type ChannelPartnerNotificationPreset,
  type ChannelPartnerService,
} from '@/api/channel-partners';
import { NOTIFICATION_PRESETS } from '../format';

type Choice = ChannelPartnerNotificationPreset | 'custom';

const MESSAGE_MAX = 1000;

export function NotifyCustomerDialog({
  service,
  onOpenChange,
}: {
  /** The service to notify about; `null` closes the dialog. */
  service: ChannelPartnerService | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [choice, setChoice] = React.useState<Choice>('document_pending');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const send = useSendChannelPartnerNotification();
  const resetSend = send.reset;
  const serviceId = service?.id;

  // Fresh form for every service the dialog opens for.
  React.useEffect(() => {
    if (!serviceId) return;
    setChoice('document_pending');
    setMessage('');
    setError(null);
    resetSend();
  }, [serviceId, resetSend]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!service) return;
    const trimmed = message.trim();
    if (choice === 'custom' && !trimmed) {
      setError('Write a message, or pick one of the ready-made reminders.');
      return;
    }
    const payload: ChannelPartnerNotificationPayload =
      choice === 'custom' ? { message: trimmed } : { preset: choice };
    setError(null);
    send.mutate(
      { serviceRequestId: service.id, payload },
      {
        onSuccess: () => {
          toast.success(
            'Notification sent',
            `${service.customer_name || 'The customer'} will see it on their dashboard shortly.`,
          );
          onOpenChange(false);
        },
        onError: (err) => {
          if (err.status === 404) {
            setError('This service is no longer available to notify about.');
          } else if (err.status === 403) {
            setError('Your Channel Partner Hub access is not active.');
          } else {
            setError(err.message || 'Could not send the notification. Please try again.');
          }
        },
      },
    );
  }

  return (
    <Dialog open={service !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Send notification</DialogTitle>
            <DialogDescription>
              To {service?.customer_name || 'the customer'} about {service?.service_name}. It
              appears on their dashboard and, where available, by email and WhatsApp.
            </DialogDescription>
          </DialogHeader>

          <div role="radiogroup" aria-label="Notification type" className="space-y-2">
            {[
              ...NOTIFICATION_PRESETS,
              {
                value: 'custom' as const,
                label: 'Custom message',
                description: 'Write your own note to the customer.',
              },
            ].map((option) => {
              const selected = choice === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setChoice(option.value);
                    setError(null);
                  }}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-md border p-3 text-left transition-colors',
                    selected
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:bg-muted',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                      selected ? 'border-accent bg-accent text-accent-foreground' : 'border-input',
                    )}
                    aria-hidden
                  >
                    {selected && <CheckCircle2 className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">{option.description}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {choice === 'custom' && (
            <div className="space-y-1.5">
              <Label htmlFor="cp-notify-message" className="flex items-center gap-1.5">
                <MessageSquareText className="h-3.5 w-3.5" aria-hidden />
                Message
              </Label>
              <Textarea
                id="cp-notify-message"
                value={message}
                maxLength={MESSAGE_MAX}
                autoFocus
                rows={4}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setError(null);
                }}
                placeholder="Please review the next step in your FinminOS dashboard."
              />
              <p className="text-right text-[11px] text-muted-foreground">
                {message.length}/{MESSAGE_MAX}
              </p>
            </div>
          )}

          <ErrorMessage message={error} />

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={send.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={send.isPending}>
              <Send className="h-4 w-4" aria-hidden />
              {send.isPending ? 'Sending…' : 'Send notification'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
